import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import LazyImage from "../../../components/LazyImage/LazyImage";
import { useModalOpen } from "../../../shared/utils/modalUtils";
import { DEFAULT_USER_AVATAR, ROLE } from "../../../shared/constants/common";
import "./profile-picture-modal.css";
import { reportError } from "../../../shared/sentry";

const IMAGE_PROTECTION_PROPS = {
  onContextMenu: (e) => e.preventDefault(),
  draggable: false,
};

const SHARE_GRID_COLUMNS = "repeat(3, 1fr)";

const ProfilePictureModal = ({
  isOpen,
  onClose,
  profilePictureUrl,
  creatorInfo,
  subscribed = false,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedInSharePopup, setCopiedInSharePopup] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);

  useModalOpen(isOpen);

  const userName = creatorInfo?.username ?? "";
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  const shareUrl = useMemo(() => {
    if (!userName) return origin;
    const base = origin.endsWith("/") ? origin.slice(0, -1) : origin;
    return `${base}/${userName}`;
  }, [origin, userName]);

  const title = useMemo(
    () => `Check out this creator on JuicyHouse: ${userName}`,
    [userName]
  );

  const handleCopyLink = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
    } catch (err) {
      reportError(err, 'ProfilePictureModal copy link');
    }
  }, [shareUrl]);

  const handleCopyLinkInSharePopup = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedInSharePopup(true);
        setTimeout(() => setCopiedInSharePopup(false), 2000);
      }
    } catch (err) {
      reportError(err, 'ProfilePictureModal copy link share popup');
    }
  }, [shareUrl]);

  const handleShareClick = useCallback(() => setShowSharePopup((prev) => !prev), []);

  const handleInstagramShare = useCallback(async () => {
    const shareData = { url: shareUrl, title, text: title };
    const canUseWebShare =
      typeof navigator !== "undefined" &&
      navigator.share &&
      (navigator.canShare ? navigator.canShare(shareData) : true);
    try {
      if (canUseWebShare) {
        await navigator.share(shareData);
      } else {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopiedInSharePopup(true);
          setTimeout(() => setCopiedInSharePopup(false), 2000);
        }
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopiedInSharePopup(true);
          setTimeout(() => setCopiedInSharePopup(false), 2000);
        }
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      } catch (fallbackErr) {
        reportError(fallbackErr, 'ProfilePictureModal Instagram share fallback');
      }
    }
  }, [shareUrl, title]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const isCreatorSub = creatorInfo?.rol === ROLE.CREATOR_SUB;
  const showFollowing =
    subscribed && (isCreatorSub || creatorInfo?.rol === ROLE.CREATOR_CONTENT);

  return createPortal(
    <div
      className="profile-picture-modal-overlay cm_bg_blur"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Profile picture"
    >
      <div className="profile-picture-modal-container" onClick={(e) => e.stopPropagation()}>
        <span
          role="button"
          tabIndex={0}
          className="cm_modal_close_btn"
          onClick={onClose}
          onKeyDown={(e) => e.key === "Enter" && onClose()}
          aria-label="Close"
        >
          <i className="fa fa-xmark" />
        </span>
        <div className="profile-picture-modal-avatar-wrap">
          <LazyImage
            {...IMAGE_PROTECTION_PROPS}
            className="profile-picture-modal-avatar"
            src={profilePictureUrl || DEFAULT_USER_AVATAR}
            alt="Profile"
          />
        </div>

        <div className="profile-picture-modal-actions">
          {showFollowing && (
            <div className="profile-picture-modal-action-item profile-picture-modal-action-label">
              <i className="fa-solid fa-user-check" />
              <span>Following</span>
            </div>
          )}
          <button
            type="button"
            className="profile-picture-modal-action-item"
            onClick={handleShareClick}
            aria-expanded={showSharePopup}
            aria-label="Share profile"
          >
            <i className="fa-solid fa-paper-plane" />
            <span>Share</span>
          </button>
          <button
            type="button"
            className="profile-picture-modal-action-item"
            onClick={handleCopyLink}
            aria-label="Copy link"
          >
            <i className="fa-solid fa-link" />
            <span>{copiedLink ? "Copied!" : "Copy link"}</span>
          </button>
        </div>

        {showSharePopup && (
          <div
            className="cm_share_popup_wraper"
            role="dialog"
            aria-modal="true"
            aria-label="Share this profile"
          >
            <div
              style={{ gridTemplateColumns: SHARE_GRID_COLUMNS }}
              className="grid gap-3 text-center text-gray-600"
            >
              <TwitterShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-x-twitter" />
              </TwitterShareButton>
              <RedditShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-reddit" />
              </RedditShareButton>
              <TelegramShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-telegram" />
              </TelegramShareButton>
              <WhatsappShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-whatsapp" />
              </WhatsappShareButton>
              <span
                onClick={handleInstagramShare}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleInstagramShare()}
                aria-label="Share on Instagram"
              >
                <i className="fa-brands fa-instagram" />
              </span>
              <span
                onClick={handleCopyLinkInSharePopup}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleCopyLinkInSharePopup()}
                title="Copy link"
                aria-label="Copy profile link"
              >
                <i className="fa-regular fa-copy" />
              </span>
            </div>
            {copiedInSharePopup && (
              <span className="mt-3 block text-center text-sm text-green-500">
                Copied!
              </span>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ProfilePictureModal;
